import { Fragment, Slice, } from "prosemirror-model";
import { wrapInList } from "prosemirror-schema-list";
import { TextSelection } from "prosemirror-state";
import { ReplaceAroundStep } from "prosemirror-transform";
import { findParentNode, } from "prosemirror-utils";
// modified from https://github.com/remirror/remirror/blob/main/packages/remirror__extension-list/src/list-commands.ts
function isList(node) {
    return ["bullet_list", "ordered_list", "check_list"].includes(node.type.name);
}
/**
 * Toggles a list item.
 *
 * When the provided list wrapper is inactive (e.g. ul) then wrap the list with
 * this type. When it is active then remove the selected line from the list.
 *
 * @param type - the list node type
 * @param itemType - the list item type (must be in the schema)
 */
export function toggleList(type, itemType) {
    return (state, dispatch) => {
        const { tr } = state;
        const { $from, $to } = tr.selection;
        const range = $from.blockRange($to);
        if (!range) {
            return false;
        }
        const parentList = findParentNode((node) => isList(node))(tr.selection);
        if (
        // the selection range is right inside the list
        parentList &&
            range.depth - parentList.depth <= 1 &&
            // the selectron range is the first child of the list
            range.startIndex === 0) {
            if (parentList.node.type === type) {
                return liftListItemOutOfList(itemType)(state, dispatch);
            }
            if (isList(parentList.node)) {
                if (type.validContent(parentList.node.content)) {
                    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(parentList.pos, type));
                    return true;
                }
                // When you try to toggle a bullet list into a task list or vice versa, since these two lists
                // use different type of list items, you can't directly change the list type.
                if (deepChangeListType(tr, parentList, type, itemType)) {
                    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.scrollIntoView());
                    return true;
                }
                return false;
            }
        }
        const listInSelection = selectionContainsList(tr);
        if (listInSelection) {
            return liftListInSelection(listInSelection)(state, dispatch);
        }
        return wrapInList(type)(state, dispatch);
    };
}
function liftListInSelection(listNode) {
    return (state, dispatch) => {
        const { tr } = state;
        const { node, pos } = listNode;
        const $from = tr.doc.resolve(pos + 1);
        const $to = tr.doc.resolve(pos + node.nodeSize - 1);
        const range = $from.blockRange($to);
        return liftOutOfList(state, dispatch, range);
    };
}
/**
 * Build a command to lift the content inside a list item around the selection
 * out of list
 */
export function liftListItemOutOfList(itemType) {
    return (state, dispatch) => {
        const { tr } = state;
        const { $from, $to } = tr.selection;
        const range = $from.blockRange($to, (node) => { var _a; return node.childCount > 0 && ((_a = node.firstChild) === null || _a === void 0 ? void 0 : _a.type) === itemType; });
        if (!range) {
            const listInSelection = selectionContainsList(tr);
            if (listInSelection) {
                return liftListInSelection(listInSelection)(state, dispatch);
            }
            return false;
        }
        if (!dispatch) {
            return true;
        }
        liftOutOfList(state, dispatch, range);
        return true;
    };
}
/**
 * Change a bullet list into a task list or vice versa. These lists use different type of list items,
 * so you need to use this function to not only change the list type but also change the list item type.
 */
function deepChangeListType(tr, foundList, listType, itemType) {
    const oldList = foundList.node;
    const $start = tr.doc.resolve(foundList.start);
    const listParent = $start.node(-1);
    const indexBefore = $start.index(-1);
    if (!listParent) {
        return false;
    }
    if (!listParent.canReplace(indexBefore, indexBefore + 1, Fragment.from(listType.create()))) {
        return false;
    }
    const newItems = [];
    for (let index = 0; index < oldList.childCount; index++) {
        const oldItem = oldList.child(index);
        if (!itemType.validContent(oldItem.content)) {
            return false;
        }
        const newItem = itemType.createChecked(null, oldItem.content);
        newItems.push(newItem);
    }
    const newList = listType.createChecked(null, newItems);
    const { from: selectionFrom, to: selectionTo } = tr.selection;
    const start = foundList.start;
    const end = start + oldList.nodeSize;
    tr.replaceRangeWith(start, end, newList);
    tr.setSelection(TextSelection.create(tr.doc, selectionFrom, Math.min(selectionTo, newList.nodeSize - 1)));
    return true;
}
// Copied from `prosemirror-schema-list`
function liftOutOfList(state, dispatch, range) {
    const tr = state.tr;
    const list = range.parent;
    // Merge the list items into a single big item
    for (let pos = range.end, i = range.endIndex - 1, e = range.startIndex; i > e; i--) {
        pos -= list.child(i).nodeSize;
        tr.delete(pos - 1, pos + 1);
    }
    const $start = tr.doc.resolve(range.start);
    const item = $start.nodeAfter;
    const atStart = range.startIndex === 0;
    const atEnd = range.endIndex === list.childCount;
    const parent = $start.node(-1);
    const indexBefore = $start.index(-1);
    if (!item) {
        return false;
    }
    if (!parent.canReplace(indexBefore + (atStart ? 0 : 1), indexBefore + 1, item.content.append(atEnd ? Fragment.empty : Fragment.from(list)))) {
        return false;
    }
    const start = $start.pos;
    const end = start + item.nodeSize;
    // Strip off the surrounding list. At the sides where we're not at
    // the end of the list, the existing list is closed. At sides where
    // this is the end, it is overwritten to its end.
    tr.step(new ReplaceAroundStep(start - (atStart ? 1 : 0), end + (atEnd ? 1 : 0), start + 1, end - 1, new Slice((atStart
        ? Fragment.empty
        : Fragment.from(list.copy(Fragment.empty))).append(atEnd ? Fragment.empty : Fragment.from(list.copy(Fragment.empty))), atStart ? 0 : 1, atEnd ? 0 : 1), atStart ? 0 : 1));
    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.scrollIntoView());
    return true;
}
/**
 * Checks if selection contains a list node and returns it.
 */
function selectionContainsList(tr) {
    const { selection: { from, to }, } = tr;
    let foundListNode = null;
    tr.doc.nodesBetween(from, to, (node, pos) => {
        if (isList(node)) {
            foundListNode = { node, pos };
        }
        if (foundListNode) {
            return false;
        }
        return true;
    });
    return foundListNode;
}
